import React from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Tag } from "antd";
import { useSelector } from "react-redux";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";

dayjs.extend(relativeTime);

const TransactionListView = (props) => {
    const { item = {}, currency } = props;
    const settings = useSelector((state) => state.settings);
    const displayCurrency = currency || settings?.currency || "INR";
    const currSymbol = getCurrencySymbol(displayCurrency);

    const { createdAt, portfolioSnapshot, type } = item;
    const snapshot = portfolioSnapshot || item || {};
    const { latest = {}, previous = {}, transactionMarketPrice } = snapshot;

    const prevCoins = Number(previous?.numberOfCoins || 0);
    const nextCoins = Number(latest?.numberOfCoins || 0);
    const coinDelta = Math.abs(nextCoins - prevCoins);
    const price = Number(transactionMarketPrice || 0);
    const invDelta = Math.abs(
        Number(latest?.investment || 0) - Number(previous?.investment || 0)
    );
    const txTotal = coinDelta * price > 0 ? coinDelta * price : invDelta;

    const typeLower = String(type || "").toLowerCase();
    const isBuy =
        typeLower === "buy" || (typeLower !== "sell" && nextCoins >= prevCoins);

    const profitColor = Tokens.profit || Tokens.positive;
    const profitBg = Tokens.profitBg || Tokens.positiveBg;
    const profitBorder = Tokens.profitBorder || Tokens.positiveBorder;
    const lossColor = Tokens.loss || Tokens.negative;
    const lossBg = Tokens.lossBg || Tokens.negativeBg;
    const lossBorder = Tokens.lossBorder || Tokens.negativeBorder;

    return (
        <div
            style={{
                width: "100%",
                marginBottom: "8px",
                padding: "14px 16px",
                borderRadius: Tokens.radiusControl,
                border: `1px solid ${Tokens.hairline}`,
                backgroundColor: Tokens.card,
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "12px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: 0,
                    }}
                >
                    <Tag
                        style={{
                            backgroundColor: isBuy ? profitBg : lossBg,
                            color: isBuy ? profitColor : lossColor,
                            border: `1px solid ${
                                isBuy ? profitBorder : lossBorder
                            }`,
                            borderRadius: Tokens.radiusTag,
                            fontWeight: 500,
                            fontSize: "11px",
                            fontFamily: Tokens.fontMono,
                            padding: "1px 8px",
                            margin: 0,
                        }}
                    >
                        {isBuy ? "Buy" : "Sell"}
                    </Tag>
                    <span
                        style={{
                            fontSize: "12px",
                            color: Tokens.textSecondary,
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {createdAt
                            ? dayjs(createdAt).format("MMM D, YYYY · HH:mm")
                            : "—"}
                    </span>
                </div>
                <span
                    style={{
                        fontSize: "12px",
                        color: Tokens.textMuted,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                    }}
                >
                    {createdAt ? dayjs(createdAt).fromNow() : ""}
                </span>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "12px",
                    alignItems: "baseline",
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: "11px",
                            color: Tokens.textSecondary,
                            marginBottom: "2px",
                        }}
                    >
                        Quantity
                    </div>
                    <div
                        style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            color: isBuy ? profitColor : lossColor,
                        }}
                    >
                        {isBuy ? "+" : "-"}
                        {getFormattedAmount(coinDelta, true, 4)}
                    </div>
                </div>

                <div>
                    <div
                        style={{
                            fontSize: "11px",
                            color: Tokens.textSecondary,
                            marginBottom: "2px",
                        }}
                    >
                        Price
                    </div>
                    <div
                        style={{
                            fontSize: "13px",
                            fontWeight: 500,
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            color: Tokens.textPrimary,
                        }}
                    >
                        {currSymbol}
                        {getFormattedAmount(price, true, 2)}
                    </div>
                </div>

                <div style={{ textAlign: "right" }}>
                    <div
                        style={{
                            fontSize: "11px",
                            color: Tokens.textSecondary,
                            marginBottom: "2px",
                        }}
                    >
                        Total
                    </div>
                    <div
                        style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            color: Tokens.textPrimary,
                        }}
                    >
                        {currSymbol}
                        {getFormattedAmount(txTotal, true, 2)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionListView;
